import cx from 'classnames';
import _ from 'lodash';
import { safeParseInt } from '../../../common/utils';
import { Children, ReactNode, cloneElement, forwardRef, DetailedHTMLProps, HTMLAttributes } from 'react';

type TSpacing = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export interface IStackProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  direction?: 'horizontal' | 'vertical';
  spacingInRems?: TSpacing | [TSpacing, TSpacing];
  gap?: number | [number, number];
  alignItems?: 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline';
  justifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
  wrap?: boolean;
  divider?: ReactNode;
  beforeChildren?: ReactNode;
  afterChildren?: ReactNode;
}

export const SPACING_VALUE = '0.125rem';

export const getSpacingValueInPx = (spacing: number) => {
  const REM_IN_PX = 16;
  const parsedSpacing = safeParseInt(SPACING_VALUE, 2);
  return parsedSpacing * REM_IN_PX * spacing;
};

export const Stack = forwardRef((props: IStackProps, ref: any) => {
  const {
    direction = 'horizontal',
    spacingInRems: spacing = 3,
    gap,
    children,
    justifyContent,
    alignItems,
    divider,
    className,
    style,
    wrap = false,
    beforeChildren,
    afterChildren,
    ...rest
  } = props;

  const [xSpacing, ySpacing] = Array.isArray(spacing) ? spacing : [spacing, spacing];

  const childrenArray = Children.toArray(children);

  const transformedChildren = childrenArray.reduce<ReactNode[]>((acc, child, index) => {
    if (index === 0) {
      return [child];
    }

    if (divider) {
      acc.push(cloneElement(<div>{divider}</div>, { key: `divider-${index}` }));
    }

    acc.push(child);
    return acc;
  }, []);

  const [xGap, yGap] = gap ? (Array.isArray(gap) ? gap : [gap, gap]) : [gap, gap];

  const isAnyGapDefined = typeof xGap === 'number' || typeof yGap === 'number';

  return (
    <div
      ref={ref}
      {...rest}
      className={cx(
        'rio-stack',
        `rio-stack--${direction}`,
        ...(!isAnyGapDefined ? [`rio-stack--spacing-x-${xSpacing}`, `rio-stack--spacing-y-${ySpacing}`] : []),
        className
      )}
      style={{
        justifyContent,
        alignItems,
        ...(typeof xGap === 'number' && { columnGap: xGap }),
        ...(typeof yGap === 'number' && { rowGap: yGap }),
        ...(wrap && { flexWrap: 'wrap' }),
        ...style
      }}
    >
      {beforeChildren}
      {transformedChildren}
      {afterChildren}
    </div>
  );
});
